import type { ReactElement } from "react";
import { Outlet, type RouteObject } from "react-router";
import { TrainingTeammatePageAchievements } from "../../features/teammatePerformance/trainingTeammatePageAchievements.js";
import { TrainingTeammatePageAutomationResults } from "../../features/teammatePerformance/trainingTeammatePageAutomationResults.js";
import { TrainingTeammatePageCalls } from "../../features/teammatePerformance/trainingTeammatePageCalls.js";
import { TrainingTeammatePageOverview } from "../../features/teammatePerformance/trainingTeammatePageOverview.js";
import { TrainingTeammatePageTraining } from "../../features/teammatePerformance/trainingTeammatePageTraining.js";
import { TrainingTeammatePage } from "../../pages/trainingTeammatePage.js";
import { appPaths } from "../appPaths.js";
import { defineHandle } from "../navigation/appRouteHandle.js";
import type { BuildCrumbs } from "../navigation/breadcrumbs.js";
import { PerformanceNavigationMenu } from "../navigation/menu/performanceNavigationMenu.js";
import { performanceSections } from "../navigation/performanceSections.js";
import { WorkspaceId } from "../navigation/workspaces.js";
import { requireRouteParam } from "../routeParams.js";
import { GradingTupleRedirect, ParamsRedirect } from "./compatRedirects.js";
import {
  createCriterionDrilldownRoute,
  createGradingSlideoverRoute,
} from "./gradingRoutes.js";

type WrapTabElement = (
  element: ReactElement,
  options: { withNavigationFallback: boolean },
) => ReactElement;

// The team-member-only Notifications list tab. The focused single-result detail
// is hoisted to a standalone route (see routeTrees) so it renders as its own
// titled page instead of under the teammate tab header.
function createAutomationResultsRoute(
  wrapTabElement: WrapTabElement,
): RouteObject {
  return {
    path: "automation-results",
    handle: defineHandle({
      crumbs: ({ pathname }) => [{ label: "Notifications", href: pathname }],
    }),
    children: [
      {
        index: true,
        element: wrapTabElement(<TrainingTeammatePageAutomationResults />, {
          withNavigationFallback: true,
        }),
      },
    ],
  };
}

/**
 * The teammate detail subtree, shared by both route trees. A factory (rather
 * than a shared constant) because both routers include the subtree.
 */
export function createTeammatePageRoute({
  crumbs,
  wrapTabElement = (element) => element,
  includesAutomationResults = false,
}: {
  crumbs: BuildCrumbs;
  /** Wraps each tab's element (team members wrap in their access guard). */
  wrapTabElement?: WrapTabElement;
  /** The team-member-only Notifications subtree. */
  includesAutomationResults?: boolean;
}): RouteObject {
  return {
    path: appPaths.teammate.pattern,
    Component: TrainingTeammatePage,
    handle: defineHandle({
      workspaceId: WorkspaceId.Performance,
      navigationMenu: <PerformanceNavigationMenu />,
      crumbs,
      pageContext: ({ params }) => ({
        page: "teammate",
        teammateId: params.teammateId,
      }),
    }),
    children: [
      {
        path: "",
        element: wrapTabElement(<TrainingTeammatePageOverview />, {
          withNavigationFallback: true,
        }),
        children: [
          createCriterionDrilldownRoute({
            path: "criteria/:criterionId",
            buildCriterionPath: (params, criterionId) =>
              appPaths.teammateCriterionCall.build({
                playbookId: requireRouteParam(params, "playbookId"),
                teammateId: requireRouteParam(params, "teammateId"),
                criterionId,
                gradingId: requireRouteParam(params, "gradingId"),
              }),
            buildTupleRedirectTo: (params, gradingId) =>
              appPaths.teammateCriterionCall.build({
                playbookId: requireRouteParam(params, "playbookId"),
                teammateId: requireRouteParam(params, "teammateId"),
                criterionId: requireRouteParam(params, "criterionId"),
                gradingId,
              }),
          }),
        ],
      },
      {
        path: "trainings/:participantId",
        element: wrapTabElement(<TrainingTeammatePageTraining />, {
          withNavigationFallback: false,
        }),
        handle: defineHandle({ crumbs: () => [{ label: "Training" }] }),
      },
      {
        path: "customer-interactions",
        element: wrapTabElement(<TrainingTeammatePageCalls />, {
          withNavigationFallback: true,
        }),
        handle: defineHandle({
          crumbs: () => [{ label: performanceSections.calls.label }],
        }),
        children: [
          createGradingSlideoverRoute({
            path: ":gradingId",
            buildCriterionPath: (params, criterionId) =>
              appPaths.teammateCallCriterion.build({
                playbookId: requireRouteParam(params, "playbookId"),
                teammateId: requireRouteParam(params, "teammateId"),
                gradingId: requireRouteParam(params, "gradingId"),
                criterionId,
              }),
          }),
        ],
      },
      // Backwards-compatibility redirects for old by-teammate URLs.
      {
        path: "accounts",
        element: (
          <ParamsRedirect
            buildTo={(params) =>
              appPaths.teammateCalls.build({
                playbookId: requireRouteParam(params, "playbookId"),
                teammateId: requireRouteParam(params, "teammateId"),
              })
            }
          />
        ),
      },
      {
        path: "accounts/:accountId/*",
        element: (
          <GradingTupleRedirect
            buildTo={(params, gradingId) =>
              appPaths.teammateCall.build({
                playbookId: requireRouteParam(params, "playbookId"),
                teammateId: requireRouteParam(params, "teammateId"),
                gradingId,
              })
            }
          />
        ),
      },
      {
        path: "achievements",
        element: wrapTabElement(<TrainingTeammatePageAchievements />, {
          withNavigationFallback: true,
        }),
        handle: defineHandle({ crumbs: () => [{ label: "Achievements" }] }),
      },
      ...(includesAutomationResults
        ? [createAutomationResultsRoute(wrapTabElement)]
        : []),
      // Backwards compatibility redirects for old routes
      {
        path: "playbook",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <ParamsRedirect
                buildTo={(params) =>
                  appPaths.teammate.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                    teammateId: requireRouteParam(params, "teammateId"),
                  })
                }
              />
            ),
          },
          {
            path: "criteria/:criterionId",
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <ParamsRedirect
                    buildTo={(params) =>
                      appPaths.teammateCriterion.build({
                        playbookId: requireRouteParam(params, "playbookId"),
                        teammateId: requireRouteParam(params, "teammateId"),
                        criterionId: requireRouteParam(params, "criterionId"),
                      })
                    }
                  />
                ),
              },
              {
                // The old tuple URL resolves its grading directly instead of
                // hopping through the (also legacy) `criteria/.../accounts/...`
                // tuple URL that used to be the target here.
                path: "accounts/:accountId",
                element: (
                  <GradingTupleRedirect
                    buildTo={(params, gradingId) =>
                      appPaths.teammateCriterionCall.build({
                        playbookId: requireRouteParam(params, "playbookId"),
                        teammateId: requireRouteParam(params, "teammateId"),
                        criterionId: requireRouteParam(params, "criterionId"),
                        gradingId,
                      })
                    }
                  />
                ),
              },
            ],
          },
        ],
      },
    ],
  };
}
